import { getResearchAdapter } from './adapter';
import { hasActiveConsent } from './consent';
import {
  listPending,
  markRetry,
  markUploaded,
  markUploading,
} from './queue';

const MAX_ATTEMPTS = 8;
const BASE_DELAY_MS = 30_000;
const MAX_DELAY_MS = 30 * 60_000;

let draining = false;
let listened = false;

export function nextRetryAt(attempts: number, now = Date.now()): number {
  const delay = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** Math.max(0, attempts - 1));
  return now + delay;
}

export function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine;
}

export async function drainResearchQueue(): Promise<number> {
  if (draining) {
    return 0;
  }
  if (!hasActiveConsent() || !isOnline()) {
    return 0;
  }
  const adapter = getResearchAdapter();
  if (!adapter.isEnabled()) {
    return 0;
  }
  draining = true;
  let uploaded = 0;
  try {
    const pending = await listPending();
    for (const item of pending) {
      if (item.id === undefined) {
        continue;
      }
      if (!hasActiveConsent()) {
        break;
      }
      try {
        await markUploading(item.id);
        await adapter.upload(item);
        await markUploaded(item.id);
        uploaded += 1;
      } catch (err) {
        const attempts = item.attempts + 1;
        const message = err instanceof Error ? err.message : String(err);
        await markRetry(
          item.id,
          attempts,
          message,
          nextRetryAt(attempts),
          attempts >= MAX_ATTEMPTS
        );
      }
    }
  } finally {
    draining = false;
  }
  return uploaded;
}

export function initResearchUploads(): void {
  if (listened || typeof window === 'undefined') {
    return;
  }
  listened = true;
  window.addEventListener('online', () => {
    void drainResearchQueue();
  });
  void drainResearchQueue();
}

export function resetDrainForTests(): void {
  draining = false;
  listened = false;
}
